import {
  Row,
  Col,
  Form,
  Input,
  Checkbox,
  FormInstance,
  Radio,
  Select,
  Alert,
} from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';

import FloatingFormItem from '@/components/_common/FloatingFormItem';
import {
  DocumentTypePaymentType,
  CreateDocumentTypeRequest,
  DocumentType,
  UpdateDocumentTypeRequest,
  DocumentTypeAccreditorType,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import {
  BankAccountInfiniteSelectField,
  InputDecimal,
  PlanAccountsSelect,
} from '@/components/_common';
import { PlanAccountHierarchy } from '@/core/domain/enterprise/PlanAccounts/PlanAccounts.types';
import { useEffect, useMemo } from 'react';
import { useDefaultEmptyFieldError } from '@/hooks';

const defaultEmptyFieldError = {
  required: true,
  message: <FormattedMessage id="commons.form.errors.requiredField" />,
};

interface AccountsDocumentTypesGeneralFormProps {
  isEdit?: boolean;
  form: FormInstance<CreateDocumentTypeRequest | UpdateDocumentTypeRequest>;
  documentType?: DocumentType;
}

function AccountsDocumentTypesGeneralForm({
  isEdit = false,
  form,
  documentType,
}: AccountsDocumentTypesGeneralFormProps) {
  const intl = useIntl();
  const ruleRequired = useDefaultEmptyFieldError();

  const initialValues = {
    creditCard: false,
    debitCard: false,
    description: '',
    fiscalDocument: false,
    fixedRate: false,
    notGenerateFinancial: false,
    payOnTime: false,
    includeBankAccountInPaymentInstallments: false,
    isPurchaseDeletionNotAllowed: false,
  };

  const hasDocumentTypeStone = !!documentType?.isStoneDocument;

  const fixedRate = Form.useWatch('fixedRate', form);
  const payOnTime = Form.useWatch('payOnTime', form);
  const accreditorType = Form.useWatch('accreditorType', form);
  const notGenerateFinancial = Form.useWatch('notGenerateFinancial', form);
  const isStoneDocument = Form.useWatch('isStoneDocument', form);
  const includeBankAccountInPaymentInstallments = Form.useWatch(
    'includeBankAccountInPaymentInstallments',
    form,
  );

  useEffect(() => {
    if (!!notGenerateFinancial) {
      form.setFieldsValue({
        fixedRate: false,
        includeBankAccountInPaymentInstallments: false,
      });
    }
  }, [notGenerateFinancial]);

  const handleBankAccountField = useMemo(() => {
    if (payOnTime || includeBankAccountInPaymentInstallments) {
      return (
        <Col xs={24} md={12} xl={12}>
          <FloatingFormItem
            name="bankAccountId"
            label={intl.formatMessage({
              id: 'documentTypes.modal.general.form.bankAccounts',
            })}
            rules={[defaultEmptyFieldError]}
          >
            <BankAccountInfiniteSelectField queryFilter={{ active: true }} />
          </FloatingFormItem>
        </Col>
      );
    }
  }, [payOnTime, includeBankAccountInPaymentInstallments]);

  const accreditorOptions = [
    {
      label: 'STONE',
      value: DocumentTypeAccreditorType.STONE,
    },
    {
      label: 'TEF_ELGIN',
      value: DocumentTypeAccreditorType.TEF_ELGIN,
    },
  ];

  return (
    <Form
      form={form}
      initialValues={initialValues}
      preserve={false}
      name="documentTypeGeneralInfo"
      layout="vertical"
    >
      <Row gutter={[8, 8]}>
        <Col xs={24} md={12} lg={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'documentTypes.modal.general.form.initials',
            })}
            name="initials"
            getValueFromEvent={({ target }) => {
              return String(target.value).toUpperCase();
            }}
            rules={[defaultEmptyFieldError, { max: 3, min: 2 }]}
          >
            <Input maxLength={3} />
          </FloatingFormItem>
        </Col>

        <Col xs={24} md={12} lg={12}>
          <FloatingFormItem
            label={intl.formatMessage({
              id: 'documentTypes.modal.general.form.description',
            })}
            getValueFromEvent={({ target }) => {
              return String(target.value).toUpperCase();
            }}
            name="description"
            rules={[defaultEmptyFieldError]}
          >
            <Input />
          </FloatingFormItem>
        </Col>

        {fixedRate && !notGenerateFinancial && (
          <Col xs={24} md={12} lg={12}>
            <Form.Item
              noStyle
              shouldUpdate={(prev, cur) => prev.fixedRate !== cur.fixedRate}
            >
              <FloatingFormItem
                label={intl.formatMessage({
                  id: 'documentTypes.modal.general.form.percentRate',
                })}
                name="percentRate"
              >
                <InputDecimal min={0} />
              </FloatingFormItem>
            </Form.Item>
          </Col>
        )}

        <Col xs={24} md={12} lg={12}>
          <FloatingFormItem
            label="Identificador de credenciadora"
            name="accreditorType"
          >
            <Select allowClear options={accreditorOptions} disabled={isEdit} />
          </FloatingFormItem>
        </Col>

        {accreditorType && !isEdit && (
          <Col span={24}>
            <Alert
              type="warning"
              description="Ao salvar o Identificador da credenciadora, essa ação será permanente. Depois de definido, não será possível alterá-lo"
              style={{ marginBottom: 24 }}
            />
          </Col>
        )}

        <Col span={24}>
          <Row gutter={24}>
            <Col span={24} md={12}>
              <FloatingFormItem valuePropName="checked" name="fiscalDocument">
                <Checkbox>
                  <FormattedMessage id="documentTypes.modal.general.form.fiscalDocument" />
                </Checkbox>
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem valuePropName="checked" name="fixedRate">
                <Checkbox disabled={notGenerateFinancial}>
                  <FormattedMessage id="documentTypes.modal.general.form.fixedRate" />
                </Checkbox>
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem
                valuePropName="checked"
                name="notGenerateFinancial"
              >
                <Checkbox disabled={payOnTime}>
                  <FormattedMessage id="documentTypes.modal.general.form.notGenerateFinancial" />
                </Checkbox>
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem valuePropName="checked" name="isStoneDocument">
                <Checkbox
                  disabled={
                    payOnTime || notGenerateFinancial || hasDocumentTypeStone
                  }
                >
                  <FormattedMessage id="documentTypes.modal.general.form.stoneDocument" />
                </Checkbox>
              </FloatingFormItem>
            </Col>

            {fixedRate && (
              <Col span={24} md={12}>
                <FloatingFormItem valuePropName="checked" name="calculateValue">
                  <Checkbox>
                    <FormattedMessage id="documentTypes.modal.general.form.calculateValue" />
                  </Checkbox>
                </FloatingFormItem>
              </Col>
            )}

            <Col span={24} md={12}>
              <FloatingFormItem valuePropName="checked" name="payOnTime">
                <Checkbox
                  disabled={
                    notGenerateFinancial ||
                    includeBankAccountInPaymentInstallments
                  }
                >
                  <FormattedMessage id="documentTypes.modal.general.form.payOnTime" />
                </Checkbox>
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem
                valuePropName="checked"
                name="includeBankAccountInPaymentInstallments"
              >
                <Checkbox disabled={payOnTime || notGenerateFinancial}>
                  <FormattedMessage id="documentTypes.modal.general.form.includeBankAccountInPaymentInstallments" />
                </Checkbox>
              </FloatingFormItem>
            </Col>

            <Col span={24} md={12}>
              <FloatingFormItem
                valuePropName="checked"
                name="isPurchaseDeletionNotAllowed"
              >
                <Checkbox>
                  A exclusão de compra não é permitida para este documento.
                </Checkbox>
              </FloatingFormItem>
            </Col>
          </Row>
        </Col>

        {!!isStoneDocument && (
          <Col span={24}>
            <Form.Item
              name="paymentType"
              label="Tipo de pagamento Stone"
              rules={[ruleRequired]}
            >
              <Radio.Group
                disabled={hasDocumentTypeStone}
                optionType="button"
                buttonStyle="solid"
                options={[
                  {
                    value: DocumentTypePaymentType.Debit,
                    label: 'Débito',
                  },
                  {
                    value: DocumentTypePaymentType.Credit,
                    label: 'Crédito',
                  },
                ]}
              />
            </Form.Item>
          </Col>
        )}

        <Col span={24}>
          <Row gutter={[24, 24]}>
            <Form.Item shouldUpdate noStyle>
              {() => (
                <>
                  {handleBankAccountField}

                  {payOnTime && (
                    <Col xs={24} md={12} xl={12}>
                      <FloatingFormItem
                        rules={[defaultEmptyFieldError]}
                        name="planAccountExpenseId"
                        label={intl.formatMessage({
                          id: 'documentTypes.modal.general.form.planAccounts',
                        })}
                      >
                        <PlanAccountsSelect
                          queryFilter={{
                            hierarchy: PlanAccountHierarchy.Analitical,
                            active: true,
                          }}
                        />
                      </FloatingFormItem>
                    </Col>
                  )}
                </>
              )}
            </Form.Item>
          </Row>
        </Col>
      </Row>
    </Form>
  );
}

export default AccountsDocumentTypesGeneralForm;
